import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { statusPillClasses, priorityClasses, statusDotClasses } from "@/lib/format";
import { applyFilters } from "@/components/FilterBar";
import StatusInline from "@/components/inline/StatusInline";
import AssigneeInline from "@/components/inline/AssigneeInline";
import PriorityInline from "@/components/inline/PriorityInline";
import DueDateInline from "@/components/inline/DueDateInline";
import TimeInline from "@/components/inline/TimeInline";

function groupTasks(tasks, groupBy, list, users) {
  if (groupBy === "status") {
    const statuses = (list.statuses || []).slice().sort((a, b) => a.order - b.order);
    return statuses.map((s) => ({
      key: s.id, label: s.name, color: s.color, kind: "status",
      tasks: tasks.filter((t) => t.status_id === s.id),
    }));
  }
  if (groupBy === "priority") {
    const order = ["urgent", "high", "medium", "low"];
    return order.map((p) => ({ key: p, label: p, kind: "priority", tasks: tasks.filter((t) => t.priority === p) }));
  }
  if (groupBy === "assignee") {
    const map = new Map();
    map.set("__none__", { key: "__none__", label: "Unassigned", kind: "assignee", tasks: [] });
    for (const u of users) map.set(u.id, { key: u.id, label: u.name, user: u, kind: "assignee", tasks: [] });
    for (const t of tasks) {
      const ids = Array.isArray(t.assignee_ids) ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
      if (ids.length === 0) {
        map.get("__none__").tasks.push(t);
      } else {
        for (const uid of ids) {
          if (!map.has(uid)) map.set(uid, { key: uid, label: "Unknown", kind: "assignee", tasks: [] });
          map.get(uid).tasks.push(t);
        }
      }
    }
    return Array.from(map.values()).filter((g) => g.tasks.length > 0);
  }
  return [{ key: "all", label: "All tasks", kind: "none", tasks }];
}

export default function ListView({ list, tasks, users, filters, groupBy, canEdit, onOpenTask, onUpdateTask, onQuickAdd, onRefresh }) {
  // Only show top-level tasks in main view (subtasks shown inside parent's panel)
  const topLevel = tasks.filter((t) => !t.parent_task_id);
  const filtered = applyFilters(topLevel, filters);
  const groups = groupTasks(filtered, groupBy, list, users);
  const [collapsed, setCollapsed] = useState({});
  const [adding, setAdding] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const toggle = (k) => setCollapsed((s) => ({ ...s, [k]: !s[k] }));

  const submitAdd = async (groupKey, group) => {
    if (!newTitle.trim()) { setAdding(null); return; }
    const overrides = {};
    if (group.kind === "status") overrides.status_id = group.key;
    if (group.kind === "priority") overrides.priority = group.key;
    if (group.kind === "assignee" && group.key !== "__none__") overrides.assignee_ids = [group.key];
    await onQuickAdd(newTitle.trim(), overrides);
    setNewTitle("");
    setAdding(null);
  };

  return (
    <div className="space-y-4" data-testid="list-view">
      {groups.length === 0 && (
        <div className="text-sm text-neutral-500 px-4 py-10 text-center bg-white border border-neutral-200 rounded-sm">No tasks match these filters.</div>
      )}
      {groups.map((g) => {
        const isCollapsed = collapsed[g.key];
        const pillCls =
          g.kind === "status" ? statusPillClasses(g.color)
            : g.kind === "priority" ? `${priorityClasses(g.key)} border`
              : "bg-neutral-900 text-white";
        return (
          <section
            key={g.key}
            className="bg-white border border-neutral-200 rounded-sm overflow-hidden"
            data-testid={`group-${g.kind}-${g.key}`}
          >
            <header
              className="px-3 py-2.5 flex items-center gap-2 border-b border-neutral-100 bg-neutral-50/60 cursor-pointer"
              onClick={() => toggle(g.key)}
            >
              <button className="text-neutral-500 hover:text-neutral-900 transition-colors" data-testid={`group-toggle-${g.key}`}>
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${pillCls}`}>
                {g.label}
              </span>
              <span className="font-mono-data text-[11px] text-neutral-500">{g.tasks.length}</span>
              {canEdit && g.kind !== "none" && (
                <button
                  onClick={(e) => { e.stopPropagation(); setAdding(g.key); setNewTitle(""); }}
                  className="ml-auto text-xs text-neutral-500 hover:text-neutral-900 transition-colors flex items-center gap-1"
                  data-testid={`group-add-task-${g.key}`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add task
                </button>
              )}
            </header>

            {!isCollapsed && (
              <>
                <div className="grid grid-cols-12 gap-2 px-3 py-2 font-mono-data text-[10px] uppercase tracking-[0.15em] text-neutral-400 border-b border-neutral-100">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Assignee</div>
                  <div className="col-span-1">Due</div>
                  <div className="col-span-1 text-right">Priority</div>
                  <div className="col-span-1 text-right">Time</div>
                </div>

                <div className="divide-y divide-neutral-100">
                  {g.tasks.length === 0 && (
                    <div className="px-4 py-3 text-xs text-neutral-400">No tasks in this group.</div>
                  )}
                  {g.tasks.map((t) => {
                    const status = (list.statuses || []).find((s) => s.id === t.status_id);
                    const subCount = tasks.filter((x) => x.parent_task_id === t.id).length;
                    return (
                      <div
                        key={t.id}
                        className="grid grid-cols-12 gap-2 px-3 py-2 hover:bg-neutral-50/60 transition-colors items-center group"
                        data-testid={`list-row-${t.id}`}
                      >
                        <div className="col-span-5 flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClasses(status?.color || "neutral")}`} />
                          <button
                            onClick={() => onOpenTask(t.id)}
                            className="text-sm font-medium truncate text-left hover:underline"
                            data-testid={`open-task-${t.id}`}
                          >
                            {t.title}
                          </button>
                          {subCount > 0 && (
                            <span className="font-mono-data text-[10px] text-neutral-400 ml-1">↳{subCount}</span>
                          )}
                        </div>
                        <div className="col-span-2">
                          <StatusInline list={list} task={t} canEdit={canEdit} onChange={(v) => onUpdateTask(t.id, { status_id: v })} />
                        </div>
                        <div className="col-span-2">
                          <AssigneeInline task={t} users={users} canEdit={canEdit} onChange={(v) => onUpdateTask(t.id, { assignee_ids: v })} />
                        </div>
                        <div className="col-span-1">
                          <DueDateInline task={t} canEdit={canEdit} onChange={(v) => onUpdateTask(t.id, { due_date: v })} />
                        </div>
                        <div className="col-span-1 text-right">
                          <PriorityInline task={t} canEdit={canEdit} onChange={(v) => onUpdateTask(t.id, { priority: v })} />
                        </div>
                        <div className="col-span-1 text-right">
                          <TimeInline task={t} canEdit={canEdit} onLogged={onRefresh} />
                        </div>
                      </div>
                    );
                  })}

                  {canEdit && adding === g.key && (
                    <div className="px-3 py-2 border-t border-neutral-100 bg-neutral-50">
                      <form onSubmit={(e) => { e.preventDefault(); submitAdd(g.key, g); }} className="flex gap-2">
                        <input
                          autoFocus
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onBlur={() => submitAdd(g.key, g)}
                          onKeyDown={(e) => { if (e.key === "Escape") { setAdding(null); setNewTitle(""); } }}
                          placeholder="Task name…"
                          className="flex-1 bg-white px-2 py-1.5 text-sm border border-neutral-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid={`group-inline-input-${g.key}`}
                        />
                      </form>
                    </div>
                  )}

                  {canEdit && adding !== g.key && g.kind !== "none" && (
                    <button
                      onClick={() => { setAdding(g.key); setNewTitle(""); }}
                      className="w-full px-3 py-2 text-left text-xs text-neutral-400 hover:text-neutral-700 hover:bg-neutral-50 flex items-center gap-1 transition-colors"
                      data-testid={`group-inline-add-${g.key}`}
                    >
                      <Plus className="w-3.5 h-3.5" /> Add task
                    </button>
                  )}
                </div>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}
